import {
  DEFAULT_CONTENTS,
  DEFAULT_SLOT_PARAMS,
  ONE_REM,
  generateForAllDevice,
} from '@@/shared-components/content/const'
import { DEFAULT_BUTTON_PARAMS } from '@@/shared-components/content/button/const'
import {
  DEFAULT_CONTAINER_DATA,
  DEFAULT_CONTAINER_PARAMS,
} from '@@/shared-components/content/container/const'
import {
  DEFAULT_EDITOR_DATA,
  DEFAULT_EDITOR_PARAMS,
} from '@@/shared-components/content/editor/const'
import { DEFAULT_GALLERY_PARAMS } from '@@/shared-components/content/gallery/const'
import {
  DEFAULT_HEADING_DATA,
  DEFAULT_HEADING_PARAMS,
  HEADINGS,
} from '@@/shared-components/content/heading/const'
import { DEFAULT_IMAGE_DATA, DEFAULT_IMAGE_PARAMS } from '@@/shared-components/content/image/const'
import {
  DEFAULT_PRODUCTS_GRID_DATA,
  DEFAULT_PRODUCTS_GRID_PARAMS,
} from '@@/shared-components/content/products/grid/const'
import { DEFAULT_SPACE_DATA, DEFAULT_SPACE_PARAMS } from '@@/shared-components/content/space/const'
import { devices } from '@@/shared-components/content/devices'
import type {
  CONTENT_STRUCTURE,
  CONTENT_STRUCTURE_WITH_DATA,
  CONTENT_STRUCTURE_DATA,
  CONTENTS,
  CONTENT_STRUCTURE_ROOT,
  SELECTED_CONTENT,
  CONTENT_STRUCTURE_SLOT,
  RAW_CONTENT_DATA,
  RAW_CLIPBOARD_PAYLOAD,
  DEVICE_TYPES,
  GenericSizeObject,
  Interactions,
} from '@@/shared-components/types/Blocks'
import { Blocks } from '~~/shared-components/content/blocks'
import { CONTENT_VERSION } from '@@/shared-components/content/const'
import { get } from 'lodash-es'
import { getLinkType } from '../composables/link'

const recalculateWidth = (
  defaultData: {
    [key in DEVICE_TYPES]: { width: GenericSizeObject }
  },
  oldData: { width: number }
) => {
  const newData = appStructuredClone(defaultData)
  Object.entries(devices).forEach(([key]) => {
    const k = key as DEVICE_TYPES
    const newWidth = (oldData?.width / 12) * 100
    newData[k].width = {
      value: isNaN(newWidth) ? 100 : newWidth,
      unit: '%',
    }
  })
  return newData
}

const refactorDataToVersion2 = (
  blockType: string,
  blockKey: UUID,
  newData: CONTENT_STRUCTURE_DATA & { type: string }
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const oldData = appStructuredClone(newData ?? {}) as any
  switch (blockType) {
    case 'text': {
      const EDITOR_DATA = DEFAULT_EDITOR_DATA()
      newData[blockKey] = {
        ...EDITOR_DATA,
        data: {
          ...EDITOR_DATA.data,
          value: oldData?.[blockKey]?.data?.text ?? EDITOR_DATA.data.value,
        },
      }
      break
    }
    case 'heading': {
      const HEADING_DATA = DEFAULT_HEADING_DATA()
      newData[blockKey] = {
        ...HEADING_DATA,
        tag:
          HEADINGS().find((h) => parseInt(h.value) === oldData[blockKey!]?.data?.heading?.type)
            ?.value ?? HEADING_DATA.tag,
        data: {
          ...HEADING_DATA.data,
          value: oldData[blockKey!]?.data?.heading?.title ?? null,
        },
      }
      break
    }
    case 'button': {
      newData[blockKey] = {
        type: 'button',
        data: {
          value: oldData[blockKey].value,
        },
      }
      break
    }
    case 'image': {
      const defaultImageData = DEFAULT_IMAGE_DATA()
      const {
        data: { image, size, crop },
      } = oldData[blockKey] ?? {}
      newData[blockKey] = {
        ...defaultImageData,
        data: { ...defaultImageData.data, image, crop, size },
      }

      break
    }
    case 'productsByCategory': {
      newData[blockKey] = DEFAULT_PRODUCTS_GRID_DATA()
      if (typeof oldData[blockKey]?.data?.category === 'number') {
        newData[blockKey].tags = [oldData[blockKey]?.data?.category]
      }
      break
    }
    case 'space': {
      newData[blockKey] = DEFAULT_SPACE_DATA()
      break
    }
    case 'container': {
      newData[blockKey] = DEFAULT_CONTAINER_DATA()
      break
    }
  }
}

const refactorDataToVersion3 = (
  blockType: string,
  blockKey: UUID,
  newData: CONTENT_STRUCTURE_DATA & { type: string }
) => {
  switch (blockType) {
    case 'container': {
      newData[blockKey] = {
        ...newData[blockKey],
        data: generateForAllDevice({
          image: null,
          size: 'l',
          crop: 'structured-image-crop-none',
        }),
      }
      break
    }
  }
}

const refactorDataToVersion4 = (
  blockType: string,
  blockKey: UUID,
  newData: CONTENT_STRUCTURE_DATA & { type: string }
) => {
  if (newData[blockKey] && newData[blockKey]?.properties?.additionalImages) {
    newData[blockKey].properties.additionalDocuments = appStructuredClone(
      newData[blockKey]?.properties?.additionalImages
    )
    delete newData[blockKey]?.properties?.additionalImages
  }
}

const refactorDataToVersion5 = (
  blockType: string,
  blockKey: UUID,
  newData: CONTENT_STRUCTURE_DATA & { type: string }
) => {
  if (newData[blockKey] && newData[blockKey]?.properties?.additionalDocuments) {
    newData[blockKey]?.properties?.additionalDocuments.forEach((path) => {
      const documentObject = get(newData[blockKey], path)
      documentObject.document = documentObject.image
      delete documentObject.image
    })
  }
}

export const refactorData = (
  blockType: string,
  blockKey: UUID,
  data: CONTENT_STRUCTURE_DATA & { type: string },
  dataVersion: number
) => {
  let version = dataVersion ?? 1
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newData = appStructuredClone(data ?? {}) as any

  while (version < CONTENT_VERSION) {
    switch (version) {
      case 1:
        refactorDataToVersion2(blockType, blockKey, newData)
        break
      case 2:
        refactorDataToVersion3(blockType, blockKey, newData)
        break
      case 3:
        refactorDataToVersion4(blockType, blockKey, newData)
        break
      case 4:
        refactorDataToVersion5(blockType, blockKey, newData)
    }
    version++
  }
  return newData[blockKey] ?? {}
}

export const refactorInteractions = (
  blockType: string,
  blockKey: UUID,
  data: CONTENT_STRUCTURE_DATA & { type: string },
  dataVersion: number
) => {
  let version = dataVersion ?? 1
  const newInteractions = {}

  while (version < CONTENT_VERSION) {
    switch (version) {
      case 1:
        refactorInteractionsToVersion2(blockType, blockKey, data, newInteractions)
        break
    }
    version++
  }
  return newInteractions
}

const refactorInteractionsToVersion2 = (
  blockType: string,
  blockKey: UUID,
  data: CONTENT_STRUCTURE_DATA & { type: string },
  newInteractions: Interactions
) => {
  switch (blockType) {
    case 'image': {
      if (data[blockKey]?.data?.link || data[blockKey]?.data?.popup) {
        const hash = crypto.randomUUID()
        const { object, type, externalUrl } = data[blockKey]?.data?.link ?? {}
        newInteractions[hash] = {
          hash,
          action:
            data[blockKey]?.data?.popup === 'lightbox'
              ? 'preview'
              : data[blockKey]?.data?.link?.type === 'external'
                ? 'linkTo'
                : 'navigateTo',
          object,
          type,
          trigger: 'onClick',
          ...(data[blockKey]?.data?.link?.type === 'external'
            ? {
                url: externalUrl,
              }
            : {}),
        }
      }
      break
    }
  }
}

const refactorParamsToVersion2 = (
  blockType: string,
  block: CONTENT_STRUCTURE_WITH_DATA,
  data: CONTENT_STRUCTURE_DATA & { type: string }
) => {
  switch (blockType) {
    case 'container':
      block.params = recalculateWidth(DEFAULT_CONTAINER_PARAMS(), block.params)
      break
    case 'text': {
      block.params = recalculateWidth(DEFAULT_EDITOR_PARAMS(), block.params)
      break
    }
    case 'heading': {
      block.params = recalculateWidth(DEFAULT_HEADING_PARAMS(), block.params)
      break
    }
    case 'image': {
      block.params = recalculateWidth(DEFAULT_IMAGE_PARAMS(), block.params)
      break
    }
    case 'link':
    case 'button': {
      block.params = recalculateWidth(DEFAULT_BUTTON_PARAMS(), block.params)
      break
    }
    case 'space': {
      block.params = recalculateWidth(DEFAULT_SPACE_PARAMS(), block.params)
      Object.entries(devices).forEach(([key]) => {
        const k = key as DEVICE_TYPES
        const spaces = {
          xs: { value: 16, unit: 'px' },
          s: { value: 32, unit: 'px' },
          m: { value: 12, unit: 'px' },
          l: { value: 64, unit: 'px' },
          xl: { value: 80, unit: 'px' },
          xxl: { value: 96, unit: 'px' },
        }
        block.params[k].value = spaces[data[block.key]?.data?.space] ?? spaces.xs
      })
      break
    }
    case 'gallery': {
      block.params = recalculateWidth(DEFAULT_GALLERY_PARAMS(), block.params)
      break
    }
    case 'productsByCategory': {
      block.params = recalculateWidth(DEFAULT_PRODUCTS_GRID_PARAMS(), block.params)
      break
    }
  }
}

const refactorParamsToVersion3 = (block) => {
  const refactorSizes = (type: string, d: string, b) => {
    if (!b.params[d][type]?.top?.unit) {
      b.params[d][type] = {
        top: {
          value: b.params[d][type].value.top,
          unit: b.params[d][type].unit,
        },
        right: {
          value: b.params[d][type].value.right,
          unit: b.params[d][type].unit,
        },
        bottom: {
          value: b.params[d][type].value.bottom,
          unit: b.params[d][type].unit,
        },
        left: {
          value: b.params[d][type].value.left,
          unit: b.params[d][type].unit,
        },
      }
    }
  }

  const refactorUnit = (type: string, d: string, b) => {
    b.params[d][type] = {
      cols: {
        value:
          b.params[d][type]?.unit === 'rem'
            ? (b.params[d][type]?.cols?.value ?? 0)
            : (b.params[d][type]?.cols?.value ?? 0) / ONE_REM,
        unit: 'rem',
      },
      rows: {
        value:
          b.params[d][type]?.unit === 'rem'
            ? (b.params[d][type]?.rows?.value ?? 0)
            : (b.params[d][type]?.rows?.value ?? 0) / ONE_REM,
        unit: 'rem',
      },
    }
  }

  Object.keys(devices).forEach((d) => {
    if (block.params?.[d]?.padding) {
      refactorSizes('padding', d, block)
    }

    if (block.params?.[d]?.margin) {
      refactorSizes('margin', d, block)
    }
    if (block.params?.[d]?.borderWidth) {
      refactorSizes('borderWidth', d, block)
    }
    if (block.params?.[d]?.borderRadius) {
      refactorSizes('borderRadius', d, block)
    }
    if (block.params?.[d]?.gap) {
      refactorUnit('gap', d, block)
    }
  })
}

const refactorParams = (
  blockType: string,
  block: CONTENT_STRUCTURE_WITH_DATA,
  data: CONTENT_STRUCTURE_DATA & { type: string },
  dataVersion: number
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newBlock = appStructuredClone(block) as any
  let version = dataVersion ?? 1
  while (version < CONTENT_VERSION) {
    switch (version) {
      case 1:
        refactorParamsToVersion2(blockType, newBlock, data)
        break
      case 2:
        refactorParamsToVersion3(newBlock)
        break
    }
    version++
  }
  return newBlock.params
}

// Posbírá z podstromu jen použité položky _contents (raw tvar pro clipboard):
// interakce (id-keyed), additionalDocuments (hash-keyed) a inline odkazy
// v parseable HTML polích ({{<linkType>:<id>|link}}, viz useEditor().hydrateLinks).
export const collectSubtreeContents = (node: CONTENT_STRUCTURE_WITH_DATA): CONTENTS => {
  const BlockInstance = Blocks.getInstance()
  const blocks = BlockInstance.getBlocks()
  const blocksChilds = Object.values(BlockInstance.getBlocksChilds()).reduce(
    (acc, nested) => ({ ...acc, ...nested }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {} as any
  )
  const out: Record<string, Record<string | number, unknown>> = {}

  const addEntry = (type: string, key: string | number, value: unknown) => {
    if (!value) return
    if (!out[type]) {
      out[type] = {}
    }
    out[type]![key] = value
  }

  const collect = (block: CONTENT_STRUCTURE_WITH_DATA) => {
    Object.entries(block.interactions ?? {}).forEach(([key, interaction]) => {
      if (interaction.type && interaction.object && typeof interaction.object === 'object') {
        addEntry(
          getLinkType(interaction.type),
          interaction.object.id ?? key,
          interaction.object
        )
      }
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const blockData = block.data as any
    const properties =
      blocks.value[blockData?.type]?.properties ??
      blocksChilds[blockData?.type]?.properties ??
      blockData?.properties

    properties?.additionalDocuments?.forEach((path: string) => {
      const value = get(blockData, path)
      if (value?.hash && block.contents?.document?.[value.hash]) {
        addEntry('document', value.hash, block.contents.document[value.hash])
      }
    })

    const blockContents = block.contents as
      | Record<string, Record<string | number, unknown>>
      | undefined

    properties?.parseable?.forEach((path: string) => {
      const html = get(blockData, path)
      if (typeof html !== 'string') return
      for (const [, linkType, id] of html.matchAll(/\{\{([\w-]+)(?::([^|]+))?\|link\}\}/g)) {
        if (!id || !linkType || ['external', 'email', 'phone', 'sms'].includes(linkType)) {
          continue
        }
        const type = getLinkType(linkType)
        addEntry(type, id, blockContents?.[type]?.[id])
      }
    })

    block.children?.forEach((child) => collect(child as CONTENT_STRUCTURE_WITH_DATA))
  }

  collect(node)
  return out as CONTENTS
}

export const useContentSerializer = () => {
  const serialize = (
    content: CONTENT_STRUCTURE_ROOT
  ): {
    structure: CONTENT_STRUCTURE
    structureContent: { [key: CONTENT_STRUCTURE['key']]: CONTENT_STRUCTURE_DATA } | null
  } => {
    const BlockInstance = Blocks.getInstance()
    const blocks = BlockInstance.getBlocks()
    const BLOCKS_CHILDS = BlockInstance.getBlocksChilds()

    const blocksChilds = Object.values(BLOCKS_CHILDS).reduce((acc, nested) => {
      return { ...acc, ...nested }
    }, {})

    const collectData = (
      node: CONTENT_STRUCTURE_ROOT | CONTENT_STRUCTURE_WITH_DATA,
      accumulator: { [key: string]: CONTENT_STRUCTURE_DATA },
      root: boolean
    ) => {
      const nodeData = appStructuredClone(node.data ?? {}) as {
        [key: string]: unknown
        type?: string
        data?: {
          image?: string | null
          dynamic_image?: string | null
          [key: string]: unknown
        }
      }
      if (node.type === 'block' || node.type === 'container') {
        const interactions = Object.fromEntries(
          Object.entries(node.interactions ?? {}).map(
            ([key, { action, hash, newWindow, object, trigger, type, url }]) => {
              let filteredInteraction = { action, trigger }
              switch (action) {
                case 'linkTo': {
                  filteredInteraction = { ...filteredInteraction, url, newWindow }
                  break
                }
                case 'scrollTo': {
                  filteredInteraction = { ...filteredInteraction, hash }
                  break
                }
                case 'navigateTo': {
                  filteredInteraction = {
                    ...filteredInteraction,
                    type,
                    object: object?.id,
                    newWindow,
                  }
                  break
                }
              }
              return [key, filteredInteraction]
            }
          )
        )
        if (typeof nodeData?.data?.image === 'string') {
          nodeData.data.dynamic_image = nodeData?.data?.image
          nodeData.data.image = null
        }
        accumulator[node.key!] = {
          type: node.type,
          ...(Object.keys(nodeData).length
            ? {
                ...nodeData,
              }
            : {}),
          ...(Object.keys(node.dynamicData ?? {}).length
            ? {
                dynamicData: node.dynamicData,
              }
            : {}),
          ...(Object.keys(interactions).length
            ? {
                interactions,
              }
            : {}),
          ...(blocks.value[nodeData.type]?.properties || blocksChilds[nodeData.type]?.properties
            ? {
                properties:
                  blocks.value[nodeData.type]?.properties ??
                  blocksChilds[nodeData.type]?.properties,
              }
            : {}),
        }
      }
      const data: CONTENT_STRUCTURE = {
        key: node.key!,
        type: node.type,
        params: node.params,
        children:
          (root ? Object.values(node.children ?? {}) : (node.children ?? [])).map((child) =>
            collectData(child, accumulator, false)
          ) ?? [],
      }
      if (node.type === 'root') {
        data.version = CONTENT_VERSION
        data.locale = node.locale
      }
      if (node.type === 'slot') {
        data.name = node.name
      }
      return data
    }

    const structureContent: { [key: CONTENT_STRUCTURE['key']]: CONTENT_STRUCTURE_DATA } = {}
    const structure = collectData(content, structureContent, true)
    return {
      structure,
      structureContent: Object.keys(structureContent).length ? structureContent : null,
    }
  }

  const generateEmptySlots = (slotNames: string[]) => {
    const slots: { [key: string]: CONTENT_STRUCTURE_SLOT } = {}
    slotNames.forEach((slotName) => {
      if (!slots[slotName]) {
        slots[slotName] = {
          name: slotName,
          type: 'slot',
          key: crypto.randomUUID(),
          children: [],
          params: DEFAULT_SLOT_PARAMS(),
        }
      }
    })
    return slots
  }

  const { dataLang } = useLocale()
  const deserialize = <T extends RAW_CONTENT_DATA>(
    data?: T,
    slotNames: string[] = []
  ): {
    id?: number
    title?: string
    _contents?: CONTENTS
    structure: SELECTED_CONTENT['content']['structure']
  } => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const defaultData = appStructuredClone(data ?? {}) as any
    try {
      if (!defaultData?.structure) {
        return {
          ...defaultData,
          structure: {
            type: 'root',
            version: CONTENT_VERSION,
            locale: dataLang.value,
            key: crypto.randomUUID(),
            children: generateEmptySlots(slotNames),
            data: {},
          },
        }
      }

      const BlockInstance = Blocks.getInstance()
      const ALL_BLOCKS = Object.fromEntries([
        ...Object.entries(BlockInstance.blocks.layout),
        ...Object.entries(BlockInstance.blocks.general),
        ...Object.entries(BlockInstance.blocks.advanced),
        ...Object.entries(BlockInstance.blocks.custom),
      ])

      const baseContents = appStructuredClone(DEFAULT_CONTENTS())
      const contentsByStructure: Record<string, Record<string, Record<string, unknown>>> = {}
      const documentContentsByHash = defaultData._contents?.document ?? {}

      Object.entries(defaultData._contents ?? {}).forEach(([contentType, contentHashs]) => {
        Object.entries(contentHashs).forEach(([hash, value]) => {
          if (!Number.isNaN(Number(hash))) {
            if (!baseContents[contentType]) {
              baseContents[contentType] = {}
            }
            baseContents[contentType][value.id] = value
            return
          }

          if (!contentsByStructure[hash]) {
            contentsByStructure[hash] = {}
          }
          if (!contentsByStructure[hash]![contentType]) {
            contentsByStructure[hash]![contentType] = {}
          }
          contentsByStructure[hash]![contentType]![value.id] = value
        })
      })

      const mergeFlatDataToStructure = (
        structure: CONTENT_STRUCTURE
      ): CONTENT_STRUCTURE_WITH_DATA => {
        const contents = appStructuredClone(baseContents)

        const needRefactor =
          !defaultData.structure!.version || defaultData.structure!.version !== CONTENT_VERSION
        const refactoredData = needRefactor
          ? refactorData(
              defaultData.structureContent?.[structure.key]?.type ?? structure.type,
              structure.key,
              defaultData.structureContent,
              defaultData.structure.version
            )
          : (defaultData.structureContent?.[structure.key] ?? {})

        const structureContents = contentsByStructure[structure.key ?? '']
        if (structureContents) {
          Object.entries(structureContents).forEach(([contentType, values]) => {
            if (!contents[contentType]) {
              contents[contentType] = {}
            }
            Object.assign(contents[contentType], values)
          })
        }

        if (refactoredData?.properties?.additionalDocuments) {
          refactoredData?.properties?.additionalDocuments?.forEach((path) => {
            const value = get(refactoredData, path)
            if (value?.document && value?.hash && documentContentsByHash?.[value?.hash]) {
              if (!contents.document) {
                contents.document = {}
              }
              contents.document[value?.hash] = documentContentsByHash[value?.hash]
            }
          })
        }

        const refactoredInteractions = Object.fromEntries(
          Object.entries(
            needRefactor
              ? refactorInteractions(
                  defaultData.structureContent?.[structure.key]?.type ?? structure.type,
                  structure.key!,
                  defaultData.structureContent,
                  defaultData.structure.version
                )
              : (defaultData.structureContent?.[structure.key]?.interactions ?? {})
          ).map(([key, interaction]) => {
            const type = getLinkType(interaction.type)
            // ID Objectu má přednost před klíčem neboť u klíče můžw být obrázek
            const object = interaction.type
              ? (contents?.[type]?.[interaction.object] ?? contents?.[type]?.[key] ?? null)
              : interaction.object
            return [key, { ...interaction, object }]
          })
        )

        if (refactoredData?.data?.dynamic_image) {
          refactoredData.data.image = refactoredData.data.dynamic_image
          delete refactoredData.data.dynamic_image
        }
        const nodeWithData: CONTENT_STRUCTURE_WITH_DATA = {
          ...ALL_BLOCKS[structure.type],
          contents,
          type: structure.type,
          key: structure.key ?? crypto.randomUUID(),
          params: needRefactor
            ? refactorParams(
                defaultData.structureContent?.[structure.key]?.type ?? structure.type,
                structure,
                defaultData.structureContent,
                defaultData.structure.version
              )
            : appStructuredClone(structure.params ?? {}),
          data: refactoredData,
          interactions: refactoredInteractions,
          dynamicData: defaultData.structureContent?.[structure.key]?.dynamicData ?? {},
          children: structure.children.map(mergeFlatDataToStructure),
        }

        if ('interactions' in nodeWithData.data) {
          delete nodeWithData.data.interactions
        }
        // if ('dynamicData' in nodeWithData.data) {
        //   delete nodeWithData.data.dynamicData
        // }
        return nodeWithData
      }
      const children = {
        ...generateEmptySlots(slotNames),
        ...Object.fromEntries(
          defaultData.structure?.children?.map((slot) => {
            return [
              slot.name ?? 'default',
              {
                ...slot,
                key: slot.key ?? crypto.randomUUID(),
                children: slot.children.map(mergeFlatDataToStructure),
                params: slot.params ?? appStructuredClone(DEFAULT_SLOT_PARAMS()),
              },
            ]
          }) ?? []
        ),
      }
      if (!defaultData.structure.locale) {
        defaultData.structure.locale = 'cs_CZ'
      }
      return {
        ...defaultData,
        structure: {
          ...defaultData.structure,
          key: defaultData.structure.key ?? crypto.randomUUID(),
          children,
        },
      }
    } catch (e) {
      console.log(e)
      console.log('ERRRRRR')
    }
  }

  const serializeBlockToClipboard = (
    block: CONTENT_STRUCTURE_WITH_DATA,
    locale?: string
  ): RAW_CLIPBOARD_PAYLOAD => {
    const root = {
      type: 'root',
      key: crypto.randomUUID(),
      locale: locale ?? dataLang.value,
      data: {},
      children: {
        default: {
          type: 'slot',
          name: 'default',
          key: crypto.randomUUID(),
          params: DEFAULT_SLOT_PARAMS(),
          children: [block],
        },
      },
    } as unknown as CONTENT_STRUCTURE_ROOT
    const { structure, structureContent } = serialize(root)
    return {
      version: CONTENT_VERSION,
      structure,
      structureContent: structureContent as RAW_CLIPBOARD_PAYLOAD['structureContent'],
      contents: collectSubtreeContents(block),
    }
  }

  const hydrateClipboardPayload = (
    payload: RAW_CLIPBOARD_PAYLOAD
  ): CONTENT_STRUCTURE_WITH_DATA | null => {
    const deserialized = deserialize(
      {
        structure: payload.structure,
        structureContent: payload.structureContent,
        _contents: payload.contents,
      } as unknown as RAW_CONTENT_DATA,
      ['default']
    )
    const slots = deserialized?.structure?.children as unknown as
      | { default?: CONTENT_STRUCTURE_SLOT }
      | undefined
    return (slots?.default?.children?.[0] as CONTENT_STRUCTURE_WITH_DATA | undefined) ?? null
  }

  return { serialize, deserialize, serializeBlockToClipboard, hydrateClipboardPayload }
}
